/**
 * Utility functions and constants for handling project status transformations
 * between backend values and UI display labels
 */

// Backend status values (as stored in database)
export const BACKEND_STATUS = {
  ACTIVE: 'Active',
  LOCKED: 'Locked',
  DROPPED: 'Dropped',
  CLOSED: 'Closed',
  CONDITIONALLY_CLOSED: 'Conditionally Closed',
};

// UI display labels (as shown to users)
export const UI_STATUS = {
  ACTIVE: 'Active',
  LOCKED: 'Locked',
  DROPPED: 'Dropped',
  CLOSED: 'Closed',
  CONDITIONAL_CLOSURE: 'Conditional Closure',
};

// Status codes
export const STATUS_CODE = {
  ACTIVE: 1,
  LOCKED: 2,
  DROPPED: 5,
  CLOSED: 6,
  CONDITIONALLY_CLOSED: 7,
};

/**
 * Transform backend status value to UI display label
 * @param {string} backendStatus - The status value from backend
 * @returns {string} The display label for UI
 */
export const transformStatusToUI = (backendStatus) => {
  if (!backendStatus) return '';
  
  const trimmedStatus = backendStatus.trim();
  
  if (trimmedStatus === BACKEND_STATUS.CONDITIONALLY_CLOSED) {
    return UI_STATUS.CONDITIONAL_CLOSURE;
  }
  
  return trimmedStatus;
};

/**
 * Transform UI display label back to backend status value
 * @param {string} uiStatus - The display label from UI
 * @returns {string} The backend status value
 */
export const transformStatusToBackend = (uiStatus) => {
  if (!uiStatus) return '';
  
  const trimmedStatus = uiStatus.trim();
  
  if (trimmedStatus === UI_STATUS.CONDITIONAL_CLOSURE) {
    return BACKEND_STATUS.CONDITIONALLY_CLOSED;
  }
  
  return trimmedStatus;
};

/**
 * Check if a status matches the conditionally closed status (case-insensitive)
 * @param {string} status - The status to check
 * @returns {boolean} True if status is conditionally closed
 */
export const isConditionallyClosedStatus = (status) => {
  if (!status || typeof status !== 'string') return false;
  
  const normalizedStatus = status.trim().toLowerCase();
  return normalizedStatus === 'conditionally closed' || normalizedStatus === 'conditional closure';
};

/**
 * Get CSS class key for status icon
 * Handles special case for conditionally closed status
 * @param {string} status - The status string
 * @returns {string} The CSS class key
 */
export const getStatusCSSKey = (status) => {
  if (!status || typeof status !== 'string') return '';
  
  const lowerStatus = status.toLowerCase();
  return lowerStatus === 'conditionally closed' || lowerStatus === 'conditional closure'
    ? 'conditionallyClosed'
    : lowerStatus;
};

/**
 * Compare two status values with case-insensitive matching
 * @param {string} actualStatus - The status value to check (from data/state)
 * @param {string} expectedStatus - The expected status value (typically from BACKEND_STATUS constants)
 * @returns {boolean} True if statuses match (case-insensitive)
 */
export const isStatusEqual = (actualStatus, expectedStatus) => {
  if (!actualStatus || !expectedStatus) return false;
  if (typeof actualStatus !== 'string' || typeof expectedStatus !== 'string') return false;
  
  return actualStatus.trim().toLowerCase() === expectedStatus.trim().toLowerCase();
};
